import React, { useState } from "react";
import {
  getContactChannel,
  LOGIN_SUPPORT_URLS,
  normalizeLoginSupportStyle,
  telegramHandleLabel,
} from "../common/loginPage";

type LoginSupportFooterProps = {
  site: any;
  children?: React.ReactNode;
};

const isChannelOn = (site: any, key: string) => {
  const value = site?.contactShowing?.[key];
  if (
    !value ||
    value === false ||
    value === "false" ||
    value === 0 ||
    value === "0"
  ) {
    return false;
  }
  return true;
};

const skypeHref = (value?: string) => {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  return /^https?:\/\//i.test(raw) ? raw : `skype:${raw}?chat`;
};

const instagramHref = (value?: string) => {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  return /^https?:\/\//i.test(raw)
    ? raw
    : `${LOGIN_SUPPORT_URLS.instagram}${raw}`;
};

const facebookHref = (value?: string) => {
  const raw = String(value || "").trim();
  if (!raw) return undefined;
  return /^https?:\/\//i.test(raw)
    ? raw
    : `https://facebook.com/${raw.replace(/^@/, "")}`;
};

const LoginSupportFooter = ({ site, children }: LoginSupportFooterProps) => {
  const loginSupportStyle = normalizeLoginSupportStyle(site?.loginSupportStyle);
  const whatsappChannel = getContactChannel(site, "whatsapp");
  const telegramChannel = getContactChannel(site, "telegram");
  const emailChannel = getContactChannel(site, "email");
  const skypeChannel = getContactChannel(site, "skype");
  const instagramChannel = getContactChannel(site, "instagram");
  const facebookChannel = getContactChannel(site, "facebook");

  const whatsappContacts = whatsappChannel.values;
  const telegramContacts = telegramChannel.values;
  const emailContacts = emailChannel.values;
  const skypeContacts = skypeChannel.values;
  const instagramContacts = instagramChannel.values;
  const showWhatsapp = isChannelOn(site, "whatsapp");
  const showSupport = isChannelOn(site, "support");
  const showTelegram = isChannelOn(site, "telegram");
  const showEmail = isChannelOn(site, "email");
  const showSkype = isChannelOn(site, "skype");
  const showInstagram = isChannelOn(site, "instagram");
  const showFacebook = isChannelOn(site, "facebook");
  const facebookValue = facebookChannel.values[0] || "";
  const hasBarSupport =
    showSupport ||
    showWhatsapp ||
    showTelegram ||
    showEmail ||
    showSkype ||
    showInstagram ||
    showFacebook;
  const telegramLabel = telegramHandleLabel(telegramContacts[0]);

  const skySupportItems = [
    {
      key: "email",
      icon: "/icons/supportemail-new.svg",
      alt: "Email",
      enabled: showEmail,
      links: emailContacts.map((contact) => ({
        label: contact,
        href: `mailto:${contact}`,
      })),
      href: emailContacts[0] ? `mailto:${emailContacts[0]}` : undefined,
      label: emailContacts[0] || "",
    },
    {
      key: "whatsapp",
      icon: "/icons/whatsapp-new.svg",
      alt: "WhatsApp",
      enabled: showWhatsapp,
      links: whatsappContacts.map((contact) => ({
        label: contact,
        href: `${LOGIN_SUPPORT_URLS.whatsapp}${contact}`,
      })),
      href: whatsappContacts[0]
        ? `${LOGIN_SUPPORT_URLS.whatsapp}${whatsappContacts[0]}`
        : undefined,
      label: whatsappContacts[0] || "",
    },
    {
      key: "telegram",
      icon: "/icons/telegram-new.svg",
      alt: "Telegram",
      enabled: showTelegram,
      links: telegramContacts.map((contact) => ({
        label: telegramHandleLabel(contact) || contact,
        href: `${LOGIN_SUPPORT_URLS.telegram}${contact}`,
      })),
      href: telegramContacts[0]
        ? `${LOGIN_SUPPORT_URLS.telegram}${telegramContacts[0]}`
        : undefined,
      label: telegramLabel,
    },
    {
      key: "skype",
      icon: "/icons/skype.svg",
      alt: "Skype",
      enabled: showSkype,
      links: skypeContacts.map((contact) => ({
        label: contact,
        href: skypeHref(contact) || "",
      })),
      href: skypeHref(skypeContacts[0]),
      label: skypeContacts[0] || "",
    },
    {
      key: "instagram",
      icon: "/icons/instagram.svg",
      alt: "Instagram",
      enabled: showInstagram,
      links: instagramContacts.map((contact) => ({
        label: contact,
        href: instagramHref(contact) || "",
      })),
      href: instagramHref(instagramContacts[0]),
      label: instagramContacts[0] || "",
    },
    {
      key: "facebook",
      icon: "/icons/facebook-new.svg",
      alt: "Facebook",
      enabled: showFacebook,
      links: facebookChannel.values.map((contact) => ({
        label: contact,
        href: facebookHref(contact) || "",
      })),
      href: facebookHref(facebookValue),
      label: facebookValue,
    },
  ];
  const visibleSkySupportItems = skySupportItems.filter((item) => item.enabled);
  const defaultSkySupportIndex =
    visibleSkySupportItems.length >= 2 ? visibleSkySupportItems.length - 2 : 0;
  const defaultSkySupportKey =
    visibleSkySupportItems[defaultSkySupportIndex]?.key || "";
  const [selectedSkySupportKey, setSelectedSkySupportKey] = useState("");
  const activeSkySupportKey =
    selectedSkySupportKey &&
      visibleSkySupportItems.some((item) => item.key === selectedSkySupportKey)
      ? selectedSkySupportKey
      : defaultSkySupportKey;
  const activeSkySupportItem = visibleSkySupportItems.find(
    (item) => item.key === activeSkySupportKey,
  );

  return (
    <div className="login_footer">
      <div className="footer">
        {loginSupportStyle === "skysupport" ? (
          <div
            className="footer_support footer_support--skysupport"
            style={{ minHeight: "28.8vw", height: "auto" }}
          >
            <div className="skysupport-icons">
              {visibleSkySupportItems.map((item) => {
                const isActive = item.key === activeSkySupportKey;
                return (
                  <div
                    key={item.key}
                    className={`skysupport-icon-wrapper ${isActive ? "active" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      setSelectedSkySupportKey(item.key);
                    }}
                  >
                    <button
                      type="button"
                      className="skysupport-icon-btn"
                      title={item.alt}
                    >
                      <img src={item.icon} alt={item.alt} />
                    </button>
                    {isActive && (
                      <div className="skysupport-notch">
                        <svg
                          width="12"
                          height="7"
                          viewBox="0 0 12 7"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M0 0L6 6L12 0Z" fill="rgba(0, 0, 0, 0.6)" />
                          <path
                            d="M0 0L6 6L12 0"
                            fill="none"
                            stroke="#c0b99d"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="skysupport-line" />
            <div className="skysupport-handle" style={{ minHeight: "12.53vw" }}>
              {activeSkySupportItem?.links &&
                activeSkySupportItem.links.length > 0 ? (
                activeSkySupportItem.links.map((linkItem, index) => (
                  <a
                    key={`${activeSkySupportItem.key}-${index}`}
                    href={linkItem.href || undefined}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {linkItem.label}
                  </a>
                ))
              ) : activeSkySupportItem && activeSkySupportItem.label ? (
                <a
                  key={activeSkySupportItem.key}
                  href={activeSkySupportItem.href}
                  target="_blank"
                  rel="noreferrer"
                >
                  {activeSkySupportItem.label}
                </a>
              ) : null}
            </div>
          </div>
        ) : loginSupportStyle === "icon-line" ? (
          <div
            className="footer_support footer_support--icon-line"
            style={{ width: "100%" }}
          >
            <div className="support-icon-row" style={{ height: "27.2vw" }}>
              <span className="support-icon-line" aria-hidden="true" />
              {showTelegram && (
                <div
                  className="support-icon-group"
                  style={{ position: "relative", top: "-6.67vw" }}
                >
                  <a
                    href={
                      telegramContacts[0]
                        ? `${LOGIN_SUPPORT_URLS.telegram}${telegramContacts[0]}`
                        : undefined
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="support-icon-link"
                    title={telegramContacts[0] || "Telegram"}
                  >
                    <img src="/images/telegram.svg" alt="Telegram" />
                  </a>
                </div>
              )}
              {showInstagram && (
                <div
                  className="support-icon-group"
                  style={{ position: "relative", top: "-6.67vw" }}
                >
                  <a
                    href={instagramHref(instagramContacts[0])}
                    target="_blank"
                    rel="noreferrer"
                    className="support-icon-link"
                    title={instagramContacts[0] || "Instagram"}
                  >
                    <img src="/images/instagram.svg" alt="Instagram" />
                  </a>
                </div>
              )}
              {showSkype && (
                <div
                  className="support-icon-group"
                  style={{ position: "relative", top: "-6.67vw" }}
                >
                  <a
                    href={skypeHref(skypeContacts[0])}
                    target="_blank"
                    rel="noreferrer"
                    className="support-icon-link"
                    title={skypeContacts[0] || "Skype"}
                  >
                    <img src="/images/skype.svg" alt="Skype" />
                  </a>
                </div>
              )}
              {showEmail && (
                <div
                  className="support-icon-group"
                  style={{ position: "relative", top: "-6.67vw" }}
                >
                  <a
                    href={
                      emailContacts[0] ? `mailto:${emailContacts[0]}` : undefined
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="support-icon-link"
                    title={emailContacts[0] || "Email"}
                  >
                    <img src="/icons/supportemail.svg" alt="Email" />
                  </a>
                </div>
              )}
              {showFacebook && (
                <div
                  className="support-icon-group"
                  style={{ position: "relative", top: "-6.67vw" }}
                >
                  <a
                    href={facebookHref(facebookValue)}
                    target="_blank"
                    rel="noreferrer"
                    className="support-icon-link"
                    title="Facebook"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="28"
                      height="28"
                      fill="#9e9e9e"
                      aria-label="Facebook"
                    >
                      <path d="M12 0C5.373 0 0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12c0-6.627-5.373-12-12-12z" />
                    </svg>
                  </a>
                </div>
              )}
              {showWhatsapp && (
                <div
                  className="support-icon-group"
                  style={{ position: "relative", top: "-6.67vw" }}
                >
                  <a
                    href={
                      whatsappContacts[0]
                        ? `${LOGIN_SUPPORT_URLS.whatsapp}${whatsappContacts[0]}`
                        : undefined
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="support-icon-link"
                    title="WhatsApp"
                  >
                    <img src="/images/whatsapp.svg" alt="WhatsApp" />
                  </a>
                </div>
              )}
              <span className="support-icon-line" aria-hidden="true" />
            </div>
          </div>
        ) : hasBarSupport ? (
          <div className="footer_support default-footer-suport-icon">
            {showSupport && (
              <div className="footer_support_item">
                <div className="extend-btn">
                  <img
                    src="/images/support.svg"
                    title="customer"
                    className="support-customer"
                    alt=""
                  />
                  <a
                    href={
                      whatsappContacts[0]
                        ? `${LOGIN_SUPPORT_URLS.whatsapp}${whatsappContacts[0]}`
                        : undefined
                    }
                    target="_blank"
                    rel="noreferrer"
                  >
                    Customer support1
                  </a>
                  <a
                    href={
                      whatsappContacts[1]
                        ? `${LOGIN_SUPPORT_URLS.whatsapp}${whatsappContacts[1]}`
                        : undefined
                    }
                    target="_blank"
                    rel="noreferrer"
                    className="split-line"
                  >
                    support2
                  </a>
                </div>
              </div>
            )}
            {showWhatsapp && (
              <div className="extend-btn">
                <a
                  href={
                    whatsappContacts[2]
                      ? `${LOGIN_SUPPORT_URLS.whatsapp}${whatsappContacts[2]}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/whatsapp.svg"
                    title="WhatsApp"
                    className="support-whatsapp"
                    alt=""
                  />
                  WhatsApp 3
                </a>
                <a
                  href={
                    whatsappContacts[3]
                      ? `${LOGIN_SUPPORT_URLS.whatsapp}${whatsappContacts[3]}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="split-line"
                >
                  WhatsApp 4
                </a>
              </div>
            )}
            {process.env.REACT_APP_TELEGRAM !== "false" && showTelegram && (
              <div className="extend-btn">
                <a
                  href={
                    telegramContacts[0]
                      ? `${LOGIN_SUPPORT_URLS.telegram}${telegramContacts[0]}`
                      : undefined
                  }
                  target="_blank"
                  rel="noreferrer"
                >
                  <img
                    src="/images/telegram.svg"
                    title="Telegram"
                    className="support-Telegram"
                    alt=""
                  />
                  Telegram
                </a>
              </div>
            )}
            {showSupport && (showSkype || showEmail || showInstagram || showFacebook) && (
              <div className="footer_support_item support-socials">
                {process.env.REACT_APP_SKYPE !== "false" && showSkype && (
                  <div className="extend-btn">
                    <img
                      src="/images/skype.svg"
                      title="Skype"
                      className="support-customer"
                      alt=""
                    />
                    <a href={skypeHref(skypeContacts[0])} target="_blank" rel="noreferrer">
                      Skype
                    </a>
                  </div>
                )}
                {process.env.REACT_APP_EMAIL !== "false" && showEmail && (
                  <div className="extend-btn">
                    <img
                      src="/images/send.svg"
                      title="Email"
                      className="support-mail"
                      alt=""
                    />
                    <a
                      href={emailContacts[0] ? `mailto:${emailContacts[0]}` : undefined}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Email
                    </a>
                  </div>
                )}
                {process.env.REACT_APP_INSTAGRAM !== "false" && showInstagram && (
                  <div className="extend-btn">
                    <img
                      src="/images/instagram.svg"
                      title="Instagram"
                      className="support-instagram"
                      alt=""
                    />
                    <a
                      href={instagramHref(instagramContacts[0])}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Instagram
                    </a>
                  </div>
                )}
                {process.env.REACT_APP_FACEBOOK !== "false" && showFacebook && (
                  <div className="extend-btn">
                    <a
                      href={facebookHref(facebookValue)}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="#757575"
                        className="support-Facebook"
                        aria-label="Facebook"
                        style={{
                          width: "4.8666666667vw",
                          height: "4.8666666667vw",
                          marginRight: "5px",
                          flexShrink: 0,
                        }}
                      >
                        <title>Facebook</title>
                        <path d="M12 0C5.373 0 0 5.373 0 12c0 5.99 4.388 10.954 10.125 11.854V15.47H7.078V12h3.047V9.356c0-3.007 1.792-4.668 4.533-4.668 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.874V12h3.328l-.532 3.469h-2.796v8.385C19.612 22.954 24 17.99 24 12c0-6.627-5.373-12-12-12z" />
                      </svg>
                      Facebook
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : null}
      </div>
      {children}
    </div>
  );
};

export default LoginSupportFooter;
